// src/services/api.js

import axios from "axios";

// =====================================================
// API BASE URL
// =====================================================

export const BASE_URL =
    process.env.REACT_APP_API_URL ||
    "http://localhost:8000";

// =====================================================
// WEBSOCKET BASE URL
// =====================================================

export const WS_BASE_URL =
    BASE_URL.replace("http", "ws");

// =====================================================
// AXIOS INSTANCE
// =====================================================

const api = axios.create({

    baseURL: BASE_URL,

    timeout: 600000,

    headers: {
        "Content-Type": "application/json"
    }
});

// =====================================================
// REQUEST INTERCEPTOR
// ATTACH JWT TOKEN
// =====================================================

api.interceptors.request.use(

    (config) => {

        const token =
            sessionStorage.getItem("token");

        if (token) {

            config.headers.Authorization =
                `Bearer ${token}`;
        }

        return config;
    },

    (error) => {

        return Promise.reject(error);
    }
);

// =====================================================
// RESPONSE INTERCEPTOR
// HANDLE AUTH ERRORS
// =====================================================

api.interceptors.response.use(

    (response) => response,

    (error) => {

        // =============================================
        // AUTO LOGOUT ON 401
        // =============================================

        if (
            error.response &&
            error.response.status === 401
        ) {

            sessionStorage.removeItem("token");

            sessionStorage.removeItem("user");

            if (
                window.location.pathname !==
                "/login"
            ) {

                window.location.href =
                    "/login";
            }
        }

        return Promise.reject(error);
    }
);

// =====================================================
// GENERIC ERROR HANDLER
// =====================================================

const handleApiError = (
    error,
    fallbackMessage
) => {

    console.error(
        "API ERROR:",
        error
    );

    if (
        error.response &&
        error.response.data
    ) {

        throw new Error(

            error.response.data.detail ||

            error.response.data.message ||

            fallbackMessage
        );
    }

    throw new Error(
        fallbackMessage
    );
};

// =====================================================
// AUTH APIs
// =====================================================

export const loginAPI = async (
    payload
) => {

    try {

        const response = await api.post(
            "/auth/login",
            payload
        );

        return response.data;

    } catch (error) {

        handleApiError(
            error,
            "Login failed"
        );
    }
};

// =====================================================

export const registerAPI = async (
    payload
) => {

    try {

        const response = await api.post(
            "/auth/register",
            payload
        );

        return response.data;

    } catch (error) {

        handleApiError(
            error,
            "Registration failed"
        );
    }
};

// =====================================================

export const getCurrentUserAPI =
    async () => {

        try {

            const response = await api.get(
                "/auth/me"
            );

            return response.data;

        } catch (error) {

            handleApiError(
                error,
                "Failed to fetch user"
            );
        }
    };

// =====================================================
// DASHBOARD APIs
// =====================================================

export const getFacilitiesAPI =
    async (date = null) => {

        try {

            const url = date
                ? `/admin/facilities?date=${date}`
                : `/admin/facilities`;

            const response = await api.get(
                url
            );

            return response.data;

        } catch (error) {

            handleApiError(
                error,
                "Failed to fetch facilities"
            );
        }
    };

// =====================================================
// FACILITY DETAILS APIs
// =====================================================

export const getFacilityDetailsAPI =
    async (facilityId) => {

        try {

            const response = await api.get(
                `/admin/facility/${facilityId}`
            );

            return response.data;

        } catch (error) {

            handleApiError(
                error,
                "Failed to fetch facility details"
            );
        }
    };

// =====================================================
// FACILITY VEHICLES APIs
// =====================================================

export const getFacilityVehiclesAPI =
    async (facilityId) => {

        try {

            const response = await api.get(
                `/admin/facility/${facilityId}/vehicles`
            );

            return response.data;

        } catch (error) {

            handleApiError(
                error,
                "Failed to fetch vehicles"
            );
        }
    };

// =====================================================
// FACILITY LAYOUT APIs
// =====================================================

export const getFacilityLayoutAPI =
    async (facilityId) => {

        try {

            const response = await api.get(
                `/admin/facility/${facilityId}/layout`
            );

            return response.data;

        } catch (error) {

            handleApiError(
                error,
                "Failed to fetch facility layout"
            );
        }
    };

// =====================================================
// SAVE FACILITY LAYOUT
// =====================================================

export const saveFacilityLayoutAPI =
    async (
        facilityId,
        payload
    ) => {

        try {

            const response = await api.post(

                `/admin/facility/${facilityId}/layout`,

                payload
            );

            return response.data;

        } catch (error) {

            handleApiError(
                error,
                "Failed to save facility layout"
            );
        }
    };

// =====================================================
// UPDATE SLOT API
// =====================================================

export const updateSlotAPI =
    async (
        facilityId,
        slotId,
        payload
    ) => {

        try {

            const response = await api.put(

                `/admin/facility/${facilityId}/slot/${slotId}`,

                payload
            );

            return response.data;

        } catch (error) {

            handleApiError(
                error,
                "Failed to update slot"
            );
        }
    };

// =====================================================
// DELETE SLOT API
// =====================================================

export const deleteSlotAPI =
    async (
        facilityId,
        slotId
    ) => {

        try {

            const response = await api.delete(

                `/admin/facility/${facilityId}/slot/${slotId}`
            );

            return response.data;

        } catch (error) {

            handleApiError(
                error,
                "Failed to delete slot"
            );
        }
    };

// =====================================================
// SLOT OCCUPANCY API
// =====================================================

export const getFacilityOccupancyAPI =
    async (facilityId) => {

        try {

            const response = await api.get(

                `/admin/facility/${facilityId}/occupancy`
            );

            return response.data;

        } catch (error) {

            handleApiError(
                error,
                "Failed to fetch occupancy"
            );
        }
    };

// =====================================================
// CREATE SLOT ASSIGNMENT
// CCTV / AI ENGINE
// =====================================================

export const createSlotAssignmentAPI =
    async (
        facilityId,
        payload
    ) => {

        try {

            const response = await api.post(

                `/admin/facility/${facilityId}/assignment`,

                payload
            );

            return response.data;

        } catch (error) {

            handleApiError(
                error,
                "Failed to create slot assignment"
            );
        }
    };

// =====================================================
// RELEASE SLOT ASSIGNMENT
// =====================================================

export const releaseSlotAssignmentAPI =
    async (
        facilityId,
        assignmentId
    ) => {

        try {

            const response = await api.put(

                `/admin/facility/${facilityId}/assignment/${assignmentId}/release`
            );

            return response.data;

        } catch (error) {

            handleApiError(
                error,
                "Failed to release slot assignment"
            );
        }
    };

// =====================================================
// HEALTH CHECK
// =====================================================

export const healthCheckAPI =
    async () => {

        try {

            const response = await api.get(
                "/health"
            );

            return response.data;

        } catch (error) {

            handleApiError(
                error,
                "Health check failed"
            );
        }
    };

// =====================================================
// EXPORT AXIOS INSTANCE
// =====================================================

export default api;